import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type EngagementOutput, type CreateEngagementInput, type UpdateEngagementInput, type UserOutput, JURISDICTION_CODES,
  ENGAGEMENT_PIPELINE_LABELS,
  ENGAGEMENT_PIPELINE_VISIBLE_STAGES,
  type FreeZoneOutput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch, captureAuthenticatedFetch } from '../lib/auth-context.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { BulkActionBar, BulkActionButton, BulkResultToast, postBulk, StaffSelect, type BulkResult } from '../components/BulkActionBar.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { ClientSearchSelect } from '../components/ClientSearchSelect.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { ViewIcon, EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { Briefcase } from 'lucide-react';
import { useEscape } from '../hooks/useEscape.js';
import { fmtDate } from '../lib/date.js';
import { DateInput } from '../components/DateInput.js';
import { useUserPreference } from '../lib/user-preferences.js';

/** Zone picked inline on the create/edit card — applied as sub-items after save. */
export type CatalogApply = { freeZoneId: string; service: string };

/** POST the business-setup catalog checklist onto an engagement; returns the toast line. */
export async function applyCatalogChecklist(engagementId: string, catalog: CatalogApply, send = captureAuthenticatedFetch()): Promise<string> {
  const res = await send(`/v1/engagements/${engagementId}/sub-items/from-catalog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
    body: JSON.stringify({ freeZoneId: catalog.freeZoneId, service: catalog.service || undefined }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Failed: ${res.status}`);
  }
  const body = await res.json() as { ok: true; data: { created: number; skipped: number; freeZoneCode: string } };
  return `Catalog applied — ${body.data.created} sub-item(s) added from ${body.data.freeZoneCode}${body.data.skipped > 0 ? `, ${body.data.skipped} already present` : ''}.`;
}

export function EngagementsPage() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // URL filters (for example dashboard tiles) take precedence over saved preferences.
  const [pipeline, setPipeline] = useUserPreference('engagements.pipeline', (() => {
    const v = new URLSearchParams(window.location.search).get('pipeline') ?? '';
    return (ENGAGEMENT_PIPELINE_VISIBLE_STAGES as readonly string[]).includes(v) ? v : '';
  })(), {
    urlKeys: ['pipeline'],
    validate: (value) => typeof value === 'string' && (value === '' || (ENGAGEMENT_PIPELINE_VISIBLE_STAGES as readonly string[]).includes(value)),
  });
  const [jurisdiction, setJurisdiction] = useUserPreference('engagements.jurisdiction', '');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<EngagementOutput | null>(null);
  const [deleting, setDeleting] = useState<EngagementOutput | null>(null);
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('clientId') || '';

  const { canDo } = useCapabilities();
  const canBulkEdit = canDo('engagements.edit');
  const canBulkArchive = canDo('engagements.delete');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'assign' | 'archive' | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Post-save note (e.g. the business-setup catalog apply result).
  const [notice, setNotice] = useState<string | null>(null);

  const { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev } = useTableControls('updatedAt', 'desc', [pipeline, clientId, search, jurisdiction]);

  // Selection only makes sense for the rows currently on screen.
  useEffect(() => { setSelected(new Set()); }, [pipeline, clientId, search, jurisdiction, sortBy, sortOrder, cursor]);

  const activeFilters: ActiveFilter[] = [
    ...(search ? [{ key: 'search', label: `Search: ${search}` }] : []),
    ...(pipeline ? [{ key: 'pipeline', label: `Pipeline: ${ENGAGEMENT_PIPELINE_LABELS[pipeline as keyof typeof ENGAGEMENT_PIPELINE_LABELS] ?? pipeline}` }] : []),
    ...(jurisdiction ? [{ key: 'jurisdiction', label: `Jurisdiction: ${jurisdiction}` }] : []),
    ...(clientId ? [{ key: 'clientId', label: `Client: ${clientId.slice(0, 8)}…` }] : []),
  ];

  function removeFilter(key: string) {
    if (key === 'search') setSearch('');
    else if (key === 'pipeline') setPipeline('');
    else if (key === 'jurisdiction') setJurisdiction('');
    else if (key === 'clientId') navigate('/engagements');
  }

  function clearAllFilters() {
    setSearch('');
    setPipeline('');
    setJurisdiction('');
    if (clientId) navigate('/engagements');
  }

  const list = useQuery({
    queryKey: ['engagements', { pipeline, clientId, search, jurisdiction, sortBy, sortOrder, cursor }],
    enabled: api !== null,
    queryFn: async () => {
      if (!api) throw new Error('not authenticated');
      const p = new URLSearchParams();
      if (pipeline) p.set('pipeline', pipeline);
      if (clientId) p.set('clientId', clientId);
      if (search) p.set('search', search);
      if (jurisdiction) p.set('jurisdiction', jurisdiction);
      p.set('all', 'true');
      p.set('limit', '50');
      p.set('sortBy', sortBy);
      p.set('sortOrder', sortOrder);
      if (cursor) p.set('cursor', cursor);
      const res = await fetch(`/v1/engagements?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: EngagementOutput[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const create = useMutation({
    mutationFn: async ({ input, catalog }: { input: CreateEngagementInput; catalog: CatalogApply | null }) => {
      const send = captureAuthenticatedFetch();
      const res = await send('/v1/engagements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(input),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const message = body?.error?.message ?? body?.error ?? res.statusText ?? `Error ${res.status}`;
        throw new Error(`Failed: ${message}`);
      }
      // Zone picked on the card → drop the checklist in right after creation.
      // A failure here never eats the engagement — it surfaces as a notice.
      if (catalog && body?.data?.id) {
        try {
          return await applyCatalogChecklist(body.data.id as string, catalog, send);
        } catch (err) {
          return `Engagement created, but the checklist apply failed: ${(err as Error).message}`;
        }
      }
      return null;
    },
    onSuccess: (catalogNote) => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      setShowNew(false);
      if (catalogNote) setNotice(catalogNote);
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch, catalog }: { id: string; patch: UpdateEngagementInput; catalog: CatalogApply | null }) => {
      const send = captureAuthenticatedFetch();
      const res = await send(`/v1/engagements/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      if (catalog) {
        try {
          return await applyCatalogChecklist(id, catalog, send);
        } catch (err) {
          return `Engagement saved, but the checklist apply failed: ${(err as Error).message}`;
        }
      }
      return null;
    },
    onSuccess: (catalogNote) => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      setEditing(null);
      if (catalogNote) setNotice(catalogNote);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/engagements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      setDeleting(null);
    },
  });

  async function runBulk(payload: { action: string; params?: Record<string, unknown> }) {
    setBulkBusy(true);
    try {
      const result = await postBulk('/v1/engagements/bulk', { ...payload, ids: [...selected] });
      setBulkResult(result);
      setBulkAction(null);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['engagements'] });
    } catch (err) {
      setBulkResult({
        updated: 0,
        failed: [...selected].map((id) => ({ id, error: err instanceof Error ? err.message : 'Bulk request failed' })),
      });
    } finally {
      setBulkBusy(false);
    }
  }

  const visibleIds = (list.data?.items ?? []).map((e) => e.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleAllVisible() {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));
  }

  function toggleSelected(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Engagements</h1>
          <p className="text-sm text-muted">{list.data?.total ?? '—'} total</p>
        </div>
        <div className="flex gap-2">
          <button
            disabled={!canDo('engagements.create')}
            onClick={() => setShowNew(true)}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            + New engagement
          </button>
        </div>
      </header>

      {notice && (
        <div className="flex items-start justify-between gap-3 bg-emerald-950/40 border border-emerald-700 text-emerald-200 text-sm rounded-lg px-4 py-2.5">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-emerald-300 hover:text-white shrink-0" aria-label="Dismiss">✕</button>
        </div>
      )}

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title or client…"
        activeFilters={activeFilters}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      >
        <select
          value={pipeline}
          onChange={(e) => setPipeline(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All pipelines</option>
          {ENGAGEMENT_PIPELINE_VISIBLE_STAGES.map((s) => (
            <option key={s} value={s}>{ENGAGEMENT_PIPELINE_LABELS[s] ?? s}</option>
          ))}
        </select>
        <select
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All jurisdictions</option>
          {JURISDICTION_CODES.map((j) => (
            <option key={j} value={j}>{j}</option>
          ))}
        </select>
      </FilterBar>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible"
                  className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                />
              </th>
              <SortableTh label="Title" sortKey="title" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Client" sortKey="client" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Jurisdiction" sortKey="jurisdiction" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Pipeline" sortKey="pipeline" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Start date" sortKey="startDate" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="End date" sortKey="endDate" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <TableSkeletonRows colSpan={8} />
            )}
            {list.error && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>
            )}
            {list.data?.items.length === 0 && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={8} onClearAll={clearAllFilters} />
                : <tr><td colSpan={8}><EmptyState icon={Briefcase} title="No engagements yet." /></td></tr>
            )}
            {list.data?.items.map((e) => (
              <tr key={e.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    onChange={() => toggleSelected(e.id)}
                    aria-label={`Select ${e.title ?? 'Untitled'}`}
                    className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                  />
                </td>
                <td className="px-4 py-3 text-white">
                  {e.title ?? 'Untitled'}
                </td>
                <td className="px-4 py-3 text-white">
                  {e.clientName ?? '—'}
                </td>
                <td className="px-4 py-3 text-muted">{e.jurisdiction ?? '—'}</td>
                <td className="px-4 py-3">
                  <EngagementPipelineBadge pipeline={e.pipeline} />
                </td>
                <td className="px-4 py-3 text-muted">{fmtDate(e.startDate)}</td>
                <td className="px-4 py-3 text-muted">{fmtDate(e.endDate)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <ViewIcon to={`/engagements/${e.id}`} label="View engagement" />
                    {canDo('engagements.edit') && <EditIcon onClick={() => setEditing(e)} label="Edit engagement" />}
                    {canDo('engagements.delete') && <DeleteIcon onClick={() => setDeleting(e)} label="Delete engagement" />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={list.data?.items.length ?? 0}
          limit={50}
          total={list.data?.total}
          hasNext={Boolean(list.data?.nextCursor)}
          onPrev={goPrev}
          onNext={() => goNext(list.data?.nextCursor)}
        />
      </div>

      {showNew && <EngagementFormModal mode="create" onClose={() => setShowNew(false)} onSubmit={(input, catalog) => create.mutate({ input: input as CreateEngagementInput, catalog })} error={create.error?.message ?? null} submitting={create.isPending} />}
      {editing && <EngagementFormModal mode="edit" engagement={editing} onClose={() => setEditing(null)} onSubmit={(patch, catalog) => update.mutate({ id: editing.id, patch, catalog })} error={update.error?.message ?? null} submitting={update.isPending} />}
      {deleting && (
        <ConfirmModal
          title="Delete engagement"
          message={`Delete engagement "${deleting.title ?? 'Untitled'}"? Soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {selected.size > 0 && (canBulkEdit || canBulkArchive) && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
          {canBulkEdit && <BulkActionButton onClick={() => setBulkAction('assign')}>Assign</BulkActionButton>}
          {canBulkArchive && <BulkActionButton danger onClick={() => setBulkAction('archive')}>Delete</BulkActionButton>}
        </BulkActionBar>
      )}

      {bulkAction === 'assign' && (
        <BulkEngagementAssignModal
          count={selected.size}
          busy={bulkBusy}
          onClose={() => setBulkAction(null)}
          onSubmit={(params) => void runBulk({ action: 'assign', params })}
        />
      )}

      {bulkAction === 'archive' && (
        <ConfirmModal
          title="Delete engagements"
          message={`Delete ${selected.size} selected engagement(s)? They are soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setBulkAction(null)}
          onConfirm={() => void runBulk({ action: 'archive' })}
          error={null}
          submitting={bulkBusy}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {bulkResult && <BulkResultToast result={bulkResult} onClose={() => setBulkResult(null)} />}
    </div>
  );
}

/** Append the UTC time suffix only to bare YYYY-MM-DD values (from input[type=date]). */
function withDateSuffix(v: string | null | undefined): string | null {
  if (!v) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00Z` : v;
}

/** Inline business-setup zone picker for the create/edit cards — the same
 * free-zone catalog the detail page's sub-items tab spawns from. Always shown:
 * an engagement IS a business setup (OPS 3.7 step 2). */
function BusinessSetupZonePicker({ zoneId, service, onChange }: {
  zoneId: string;
  service: string;
  onChange: (zoneId: string, service: string) => void;
}) {
  const freeZones = useQuery({
    queryKey: ['free-zones', 'engagement'],
    queryFn: async () => {
      const res = await fetch('/v1/free-zones', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: FreeZoneOutput[] } };
      return body.data.items;
    },
  });

  const selectedZone = (freeZones.data ?? []).find((z) => z.id === zoneId) ?? null;
  const services = [...new Set((selectedZone?.items ?? []).map((i) => i.service))];

  return (
    <div className="border border-accent/40 bg-accent/5 rounded-lg p-3 space-y-2">
      <label className="block text-xs text-muted">Business setup zone — checklist applies when you save</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select
          value={zoneId}
          onChange={(e) => onChange(e.target.value, '')}
          className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">Select free zone… (optional)</option>
          {(freeZones.data ?? []).map((z) => <option key={z.id} value={z.id}>{z.code} — {z.name}</option>)}
        </select>
        <select
          value={service}
          onChange={(e) => onChange(zoneId, e.target.value)}
          disabled={!selectedZone}
          className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent disabled:opacity-50"
        >
          <option value="">All services in zone</option>
          {services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {selectedZone && (
        <p className="text-xs text-muted">
          {(service ? selectedZone.items.filter((i) => i.service === service) : selectedZone.items).length} checklist item(s) will be added on save (already-present items are skipped).
        </p>
      )}
      {freeZones.error && <p className="text-xs text-rose-400">{(freeZones.error as Error).message}</p>}
    </div>
  );
}

/** OPS 3.5 #9: ONE engagement form for create AND edit.
 *  OPS 3.7 step 2: engagement IS business setup — the interest field and the
 *  pipeline stage selector are gone from the card (stages move only via the
 *  proposal status chain); the business-setup zone picker is always shown.
 *  OPS 3.5 #1/#5: jurisdiction prefills from the picked client (still manually
 *  overridable — business setup can target a different jurisdiction than the
 *  client's home).
 *  OPS 3.5 #4: the dead `type` field is gone from the card (engagements are
 *  one-off only since 3.3); the DB column + API field stay for back-compat.
 *  OPS 3.5 #7: no kick-off task / renewal date fields here — renewals live in
 *  compliance. OPS 3.5 C: kick-off is gone entirely (the proposal signature
 *  chain owns pipeline progression). */
export function EngagementFormModal({ mode, engagement, onClose, onSubmit, error, submitting }: {
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  engagement?: EngagementOutput;
  onClose: () => void;
  /** Edit payload is UpdateEngagementInput (clientId optional there); the
   *  create call site casts to CreateEngagementInput. */
  onSubmit: (input: UpdateEngagementInput, catalog: CatalogApply | null) => void;
  error: string | null;
  submitting: boolean;
}) {
  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const [form, setForm] = useState<UpdateEngagementInput>(() => mode === 'edit' && engagement ? {
    title: engagement.title,
    jurisdiction: engagement.jurisdiction,
    managerId: engagement.managerId,
    agentId: engagement.agentId,
    startDate: engagement.startDate?.slice(0, 10) ?? '',
    endDate: engagement.endDate?.slice(0, 10) ?? '',
  } : {
    clientId: '',
    title: '',
    jurisdiction: null,
    managerId: null,
    agentId: null,
    startDate: null,
    endDate: null,
  });
  // Business-setup zone picked inline (applied as sub-items after save).
  const [zoneId, setZoneId] = useState('');
  const [catalogService, setCatalogService] = useState('');

  function update<K extends keyof UpdateEngagementInput>(k: K, v: UpdateEngagementInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const canSubmit = Boolean(
    (mode === 'edit' || form.clientId) &&
    form.title && form.jurisdiction &&
    form.managerId && form.agentId && form.startDate && form.endDate
  );

  return (
    <ModalShell title={mode === 'create' ? 'New engagement' : 'Edit engagement'} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          const payload = {
            ...form,
            startDate: withDateSuffix(form.startDate),
            endDate: withDateSuffix(form.endDate),
          };
          onSubmit(payload, zoneId ? { freeZoneId: zoneId, service: catalogService } : null);
        }}
        className="space-y-3"
      >
        {mode === 'create' && (
          <div>
            <label className="block text-xs text-muted mb-1">Client <span className="text-rose-400">*</span></label>
            <ClientSearchSelect
              value={form.clientId ?? ''}
              onChange={(v, c) => {
                update('clientId', v);
                // #1: prefill jurisdiction from the client — stays overridable.
                if (c?.jurisdiction) update('jurisdiction', c.jurisdiction as UpdateEngagementInput['jurisdiction']);
              }}
              placeholder="— select —"
              required
            />
            <p className="text-xs text-muted mt-1">CRM client this business setup is for.</p>
          </div>
        )}
        <div>
          <label className="block text-xs text-muted mb-1">Title <span className="text-rose-400">*</span></label>
          <input
            value={form.title ?? ''}
            onChange={(e) => update('title', e.target.value || null)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            required
          />
          <p className="text-xs text-muted mt-1">Short engagement name, e.g. "Company setup — Acme Ltd".</p>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Jurisdiction <span className="text-rose-400">*</span></label>
          <select
            value={form.jurisdiction ?? ''}
            onChange={(e) => update('jurisdiction', e.target.value || null)}
            className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
            required
          >
            <option value="">—</option>
            {JURISDICTION_CODES.map((j) => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
          <p className="text-xs text-muted mt-1">{mode === 'create' ? 'Prefilled from the client — change it if the work targets another jurisdiction.' : 'Country/region the work covers, e.g. UK.'}</p>
        </div>
        <BusinessSetupZonePicker zoneId={zoneId} service={catalogService} onChange={(z, s) => { setZoneId(z); setCatalogService(s); }} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Manager <span className="text-rose-400">*</span></label>
            <SearchableSelect
              value={form.managerId ?? ''}
              onChange={(v) => update('managerId', v || null)}
              placeholder="—"
              required
              options={(users.data ?? []).filter((u) => u.status !== "DISABLED").map((u) => ({ value: u.id, label: `${u.displayName} (${u.email})` }))}
            />
            <p className="text-xs text-muted mt-1">Person overseeing this engagement.</p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Assignee <span className="text-rose-400">*</span></label>
            <SearchableSelect
              value={form.agentId ?? ''}
              onChange={(v) => update('agentId', v || null)}
              placeholder="—"
              required
              options={(users.data ?? []).filter((u) => u.status !== "DISABLED").map((u) => ({ value: u.id, label: `${u.displayName} (${u.email})` }))}
            />
            <p className="text-xs text-muted mt-1">Person doing the day-to-day work.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Start date <span className="text-rose-400">*</span></label>
            <DateInput
              value={form.startDate ?? ''}
              onChange={(e) => update('startDate', e.target.value || null)}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
              required
            />
            <p className="text-xs text-muted mt-1">When work begins.</p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">End date <span className="text-rose-400">*</span></label>
            <DateInput
              value={form.endDate ?? ''}
              onChange={(e) => update('endDate', e.target.value || null)}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
              required
            />
            <p className="text-xs text-muted mt-1">Expected completion date.</p>
          </div>
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={submitting || !canSubmit} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{submitting ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create engagement' : 'Save changes')}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ConfirmModal({ title, message, onClose, onConfirm, error, submitting, confirmText, confirmClass }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
  submitting: boolean;
  confirmText: string;
  confirmClass: string;
}) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={onConfirm} disabled={submitting} className={`text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 ${confirmClass}`}>{submitting ? 'Working…' : confirmText}</button>
        </div>
      </div>
    </div>
  );
}

function EngagementPipelineBadge({ pipeline }: { pipeline: string }) {
  const active = ['ENGAGED'];
  const colour = active.includes(pipeline)
    ? 'pill-success'
    : pipeline === 'NOT_INTERESTED' || pipeline === 'DEFERRED'
    ? 'pill-danger'
    : 'pill-warn';
  const label = ENGAGEMENT_PIPELINE_LABELS[pipeline] ?? pipeline;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${colour}`}>{label}</span>;
}


function BulkEngagementAssignModal({ count, busy, onClose, onSubmit }: {
  count: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (params: Record<string, unknown>) => void;
}) {
  const [managerId, setManagerId] = useState<string | null | undefined>(undefined);
  const [agentId, setAgentId] = useState<string | null | undefined>(undefined);
  const canSubmit = managerId !== undefined || agentId !== undefined;
  return (
    <ModalShell title={`Assign ${count} engagement(s)`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Manager</label>
          <StaffSelect value={managerId} onChange={setManagerId} />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Agent</label>
          <StaffSelect value={agentId} onChange={setAgentId} />
        </div>
        <p className="text-xs text-muted">Fields left on “keep current” stay unchanged on every selected engagement.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={() => {
              const params: Record<string, unknown> = {};
              if (managerId !== undefined) params.managerId = managerId;
              if (agentId !== undefined) params.agentId = agentId;
              onSubmit(params);
            }}
            disabled={busy || !canSubmit}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {busy ? 'Applying…' : 'Assign'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
